// COP — the "VIEW AS" control surface: the compact fly-out summoned from the ROW-1
// banner cue that composes a voluntary decision-time DESCOPE (view the picture as a
// lower ceiling / a narrower releasability / with SCI+caveats dropped / as a named
// partner profile).
// CONTRACT:I4-POLICY-DECISION.1.0 — the panel edits a DescopeSpec and hands it to the
//          host's setViewOverlay seam; the host applies it through host/descope.ts
//          (descopedClaimsFor / specDominatedByHeld) — the ONE place narrowHeld is called.
// Architecture: docs/ux/SHELL-COMPOSITION.md §"view-as" — a subtractive, Glomar-safe
//          preview control. It can only ever hide MORE; the exactness flag warns when the
//          preview is STRICTER than the named target (the operator does not hold everything
//          that target does), so an operator never mistakes a stricter preview for parity.
//
// SEAM NOTE (parallel lanes): the LEVEL CEILING dropdown drives the EXISTING, proven
// down-filter seam (onQueryLevel → setQueryLevel) so ROW-1 re-colors/re-labels to the
// chosen level exactly as before. The dimensional axes (releasability / SCI / caveat /
// partner presets) drive the NEW overlay seam (onViewOverlay → setViewOverlay). The host
// composes the two (queryLevel clamp ∧ overlay narrowing).

import { useState } from "react";
import type { QueryScope } from "../host/cop.ts";
import type { DescopeSpec } from "../host/descope.ts";
import { classificationLabel } from "./dod-marking.ts";
import { useEscapeStack } from "./useEscapeStack.ts";
import "./ViewAsPanel.css";

/** The active view-as overlay the host surfaces on `status.queryScope.viewAs`. The host
 *  lane (cop.ts) owns the canonical definition; this is the UI consumption shape. `exact`
 *  is specDominatedByHeld(realClaims, spec) — false ⇒ the preview is STRICTER than `label`. */
export interface ViewAsOverlay {
  spec: DescopeSpec;
  label: string;
  exact: boolean;
}

/** Held policy-attribute values per bundle axis, surfaced by the host on
 *  `status.queryScope.heldValues` for the panel to render "keep only held" controls.
 *  Keyed by EXACT bundle axis name. Optional — the panel derives a best-effort fallback
 *  from queryHighWater when the host has not surfaced it (see heldAxisValues + INTERFACE). */
export type HeldAxisValues = Record<string, string[]>;

/** The host-surfaced fields the UI reads OFF the QueryScope (consumed via a structural
 *  cast so this lane typechecks in isolation before the host lane merges the fields). */
export interface QueryScopeViewAs {
  viewAs?: ViewAsOverlay | null;
  heldValues?: HeldAxisValues;
}

/** The host imperative the panel calls to apply/clear the overlay. `setViewOverlay(null)`
 *  clears it. Consumed via a structural cast against CopHandle (host lane owns the method). */
export interface ViewOverlayHandle {
  setViewOverlay(spec: DescopeSpec | null): void;
}

// --- Bundle axis names (EXACT — never "classification_level"; the seam rejects it). ---
const AX_RELEASABILITY = "releasability";
const AX_SCI_CONTROL = "sci_control_system";
const AX_SCI_COMPARTMENT = "sci_compartment";
const AX_HANDLING = "handling_caveat";

/** NAMED PARTNER PRESETS — canonical clearance-profile specs over the policy vocab. These
 *  are illustrative "view as X" profiles, NOT derived from persona metadata. Source of
 *  truth for the vocabulary is the policy bundle; a future roster-profile file may replace
 *  these hardcoded specs. narrowHeld intersects each spec with what the operator HOLDS, so
 *  a preset value the operator lacks simply contributes nothing (and flips `exact` false). */
const PARTNER_PRESETS: ReadonlyArray<{ id: string; label: string; spec: DescopeSpec }> = [
  { id: "gbr", label: "GBR analyst", spec: { [AX_RELEASABILITY]: ["GBR"] } },
  { id: "fvey", label: "FVEY", spec: { [AX_RELEASABILITY]: ["USA", "GBR", "CAN", "AUS", "NZL", "FVEY"] } },
  { id: "nato", label: "NATO", spec: { [AX_RELEASABILITY]: ["NATO"] } },
];

/** Held values per axis, from the host-surfaced per-axis `heldValues` (releasability,
 *  sci_control_system, sci_compartment, handling_caveat — proper-cased, un-narrowed). A descope
 *  can only ever KEEP a value the operator holds, so this is the exact control palette; correct
 *  at every level (unlike the level-gated, SCI-merged queryHighWater). */
function heldAxisValues(queryScope: QueryScope): HeldAxisValues {
  const surfaced = (queryScope as unknown as QueryScopeViewAs).heldValues ?? {};
  return {
    [AX_RELEASABILITY]: surfaced[AX_RELEASABILITY] ?? [],
    [AX_HANDLING]: surfaced[AX_HANDLING] ?? [],
    [AX_SCI_CONTROL]: surfaced[AX_SCI_CONTROL] ?? [],
    [AX_SCI_COMPARTMENT]: surfaced[AX_SCI_COMPARTMENT] ?? [],
  };
}

/** Same-membership check (order-independent) for "is this kept-set the full held set?". */
function sameSet(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  const set = new Set(a);
  return b.every((v) => set.has(v));
}

/** Strip the legacy classification_level key (the seam rejects it) — belt-and-suspenders;
 *  the panel never writes it, but a seeded overlay spec might carry it. */
function stripLegacy(spec: DescopeSpec): DescopeSpec {
  if (!("classification_level" in spec)) return spec;
  const clean: DescopeSpec = {};
  for (const [k, v] of Object.entries(spec)) if (k !== "classification_level") clean[k] = v;
  return clean;
}

export function ViewAsPanel({
  queryScope,
  viewAs,
  onQueryLevel,
  onViewOverlay,
  onClose,
}: {
  queryScope: QueryScope;
  viewAs: ViewAsOverlay | null;
  /** LEVEL CEILING — the EXISTING down-filter seam (unchanged). */
  onQueryLevel: (level: string) => void;
  /** Dimensional descope — the NEW overlay seam. `null` clears the overlay. */
  onViewOverlay: (spec: DescopeSpec | null) => void;
  onClose: () => void;
}): JSX.Element {
  useEscapeStack(onClose, true);

  const held = heldAxisValues(queryScope);
  // Levels HIGH → LOW so "full clearance" sits at the top of the ceiling menu.
  const levelOptions = [...queryScope.available].reverse();
  const canPickLevel = queryScope.available.length > 1;

  // The dimensional DRAFT (never carries a classification axis — the level goes through
  // onQueryLevel). Seeded from the active overlay so re-opening shows current selections.
  const [draft, setDraft] = useState<DescopeSpec>(() => {
    const seed = stripLegacy(viewAs?.spec ?? {});
    const d: DescopeSpec = {};
    for (const ax of [AX_RELEASABILITY, AX_SCI_CONTROL, AX_SCI_COMPARTMENT, AX_HANDLING]) {
      if (ax in seed) d[ax] = [...(seed[ax] ?? [])];
    }
    return d;
  });

  // The KEPT set for an axis = the draft value if narrowed, else the full held set.
  const keptFor = (axis: string): string[] => draft[axis] ?? held[axis] ?? [];

  // Toggle one value's membership in an axis's kept set. When the result equals the full
  // held set, DROP the axis from the spec (minimal spec = no needless narrowing).
  const toggle = (axis: string, value: string): void => {
    setDraft((prev) => {
      const heldVals = held[axis] ?? [];
      const current = prev[axis] ?? heldVals;
      const next = current.includes(value)
        ? current.filter((v) => v !== value)
        : heldVals.filter((v) => current.includes(v) || v === value); // preserve held order
      const out: DescopeSpec = { ...prev };
      if (sameSet(next, heldVals)) delete out[axis];
      else out[axis] = next;
      return out;
    });
  };

  const applyPreset = (spec: DescopeSpec): void => {
    // Presets REPLACE the dimensional draft (a partner profile is one deliberate choice).
    const next: DescopeSpec = {};
    for (const [k, v] of Object.entries(stripLegacy(spec))) next[k] = [...v];
    setDraft(next);
  };

  const hasDraft = Object.keys(draft).length > 0;
  const apply = (): void => onViewOverlay(hasDraft ? draft : null);
  const exit = (): void => {
    setDraft({});
    onViewOverlay(null);
  };

  const overlayActive = viewAs != null;

  const axisSection = (title: string, hint: string, axis: string): JSX.Element => {
    const heldVals = held[axis] ?? [];
    const kept = keptFor(axis);
    return (
      <div className="viewas-section">
        <div className="viewas-section__label">
          {title}
          <span className="viewas-section__hint"> · {hint}</span>
        </div>
        {heldVals.length === 0 ? (
          <div className="viewas-empty">none held</div>
        ) : (
          <div className="viewas-axis">
            {heldVals.map((v) => {
              const on = kept.includes(v);
              return (
                <label key={v} className={`viewas-chk${on ? "" : " viewas-chk--dropped"}`}>
                  <input type="checkbox" checked={on} onChange={() => toggle(axis, v)} />
                  <span>{v}</span>
                </label>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="viewas-panel" role="dialog" aria-label="View as — voluntary descope preview">
      <div className="viewas-panel__header">
        <span className="viewas-panel__title">VIEW AS</span>
        <button type="button" className="viewas-panel__close" aria-label="Close view-as panel" onClick={onClose}>
          ✕
        </button>
      </div>

      {/* (a) LEVEL CEILING — the existing down-filter seam, relocated here. */}
      <div className="viewas-section">
        <div className="viewas-section__label">
          LEVEL CEILING<span className="viewas-section__hint"> · working level ≤ your clearance</span>
        </div>
        <select
          className="viewas-level"
          value={queryScope.queryLevel}
          disabled={!canPickLevel}
          onChange={(e) => onQueryLevel(e.target.value)}
          aria-label="Down-filter the working clearance level"
        >
          {levelOptions.map((lvl) => (
            <option key={lvl} value={lvl}>
              {classificationLabel(lvl)}
              {lvl === queryScope.clearanceLevel ? " · full" : ""}
            </option>
          ))}
        </select>
      </div>

      {/* (b) RELEASABILITY SUBSET — keep only held values (uncheck to drop). */}
      {axisSection("RELEASABILITY", "keep held; uncheck to drop", AX_RELEASABILITY)}

      {/* (c) SCI DROPS — held control systems + compartments. */}
      {axisSection("SCI CONTROL SYSTEM", "uncheck to drop", AX_SCI_CONTROL)}
      {axisSection("SCI COMPARTMENT", "uncheck to drop", AX_SCI_COMPARTMENT)}

      {/* (d) CAVEAT DROPS — held handling caveats. */}
      {axisSection("HANDLING CAVEATS", "uncheck to drop", AX_HANDLING)}

      {/* (e) NAMED PARTNER PRESETS — canonical clearance-profile specs. */}
      <div className="viewas-section">
        <div className="viewas-section__label">PARTNER PRESETS<span className="viewas-section__hint"> · view as a profile</span></div>
        <div className="viewas-presets">
          {PARTNER_PRESETS.map((p) => (
            <button key={p.id} type="button" className="viewas-preset" onClick={() => applyPreset(p.spec)}>
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* (f) EXACTNESS FLAG — the preview is STRICTER than the named target. */}
      {overlayActive && viewAs.exact === false && (
        <div className="viewas-exact" role="note">
          <span aria-hidden="true">▲</span> Preview is STRICTER than a real {viewAs.label} — you don&rsquo;t
          hold everything they do.
        </div>
      )}

      <div className="viewas-actions">
        <button type="button" className="viewas-btn viewas-btn--apply" onClick={apply}>
          Apply
        </button>
        {/* (g) one-click EXIT — clears the overlay. Prominent. */}
        <button
          type="button"
          className="viewas-btn viewas-btn--exit"
          onClick={exit}
          disabled={!overlayActive && !hasDraft}
        >
          Exit view-as
        </button>
      </div>
    </div>
  );
}
